import {
  axiosApplication,
  axiosDashboard,
  axiosFile
} from "@config/axiosConfig";

export interface DemandDraftListPayload {
  access_id?: number;
  page?: number;
  limit?: number;
  searchTerm?: string;
  state?: string;
}

export interface DemandDraftItem {
  application_code: string;
  demand_draft_url: string;
  applicant_name: string;
  business_name: string;
  state: string;
  utility_type: string;
  amenity_name: string;
  start_chainage: string;
  end_chainage: string;
  address_1: string;
  address_2: string;
  district: string;
  village: string;
  morth_authority: string;
  highway_category: string;
  national_highway_number: string;
  issue_date: string;
}

export interface DemandDraftListResponse {
  data: DemandDraftItem[];
  total: number;
}

export interface StateOption {
  label: string;
  value: string;
  code?: string;
}
type StateApiItem = {
  statename: string;
  stateShortCode: string;
};

export const getStateList = async (): Promise<StateOption[]> => {
  try {
    const res = await axiosDashboard.get<{
      success?: boolean;
      message?: string;
      data: StateApiItem[];
    }>("/state-list", { withCredentials: true });

    const payload: any = res?.data;
    const list: StateApiItem[] = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
        ? payload
        : [];

    return list.map(it => ({
      label: it.statename,
      value: it.statename,
      code: it.stateShortCode
    }));
  } catch (e) {
    throw e;
  }
};

const PUBLIC_COMMENTS_LIST_PATH =
  process.env.NEXT_PUBLIC_PUBLIC_COMMENTS_PATH || "/public-comments-list";

export const getPublicCommentsDetails = async (
  payload: DemandDraftListPayload
) => {
  return axiosApplication.post<{
    success: boolean;
    message: string;
    data: DemandDraftListResponse;
  }>(PUBLIC_COMMENTS_LIST_PATH, payload, {
    headers: { "Content-Type": "application/json" }
  });
};

// Guidelines

const PUBLISHED_GUIDELINES_PATH =
  process.env.NEXT_PUBLIC_PUBLISHED_GUIDELINES_PATH || "/published-guidelines";

export interface GuidelineDocument {
  document_id: number;
  document_name: string | null;
  key_name: string | null;
  document_type: string;
}

export interface GuidelineApiItem {
  id: number;
  title: string;
  category: string;
  created_at: string;
  updated_at: string;
  published_on_date?: string;
  document?: GuidelineDocument;
}

export interface PublishedGuidelinesData {
  general_guidelines?: GuidelineApiItem[];
  permission_guidelines?: GuidelineApiItem[];
}
export interface PublishedGuidelinesResponse {
  success: boolean;
  message: string;
  data: PublishedGuidelinesData;
}

export const getGuidelinesList = async (category?: string) => {
  return axiosApplication.get<PublishedGuidelinesResponse>(
    PUBLISHED_GUIDELINES_PATH,
    {
      params: category ? { category } : undefined,
      headers: { "Content-Type": "application/json" }
    }
  );
};

// Public file viewer API — choose a safe fileName (prefer processed key_name), encode params and validate
export const getPublicUrlAPI = async ({
  fileName,
  document_name,
  key_name,
  moduletype,
  folderName
}: any) => {
  let file = fileName ?? undefined;

  if (!file && key_name) {
    if (typeof key_name === "string") {
      const parts = key_name.split("/");
      file = parts.length > 0 ? parts[parts.length - 1] : key_name;
    } else {
      file = undefined;
    }
  }

  if (!file && document_name) {
    file = document_name;
  }

  if (!file) {
    throw new Error(
      "No fileName or key_name/document_name provided for public-view-license"
    );
  }

  const moduleType = moduletype ? String(moduletype).toLowerCase() : "";
  const encodedFile = encodeURIComponent(file);
  const encodedModule = encodeURIComponent(moduleType);
  const encodedFolder = folderName ? encodeURIComponent(folderName) : undefined;

  let url = `/public-view-license?fileName=${encodedFile}&moduletype=${encodedModule}`;

  if (encodedFolder) {
    url += `&folderName=${encodedFolder}`;
  }
  return await axiosFile.get(url, {
    responseType: "blob"
  });
};

// Notifications

const NOTIFICATIONS_PATH =
  process.env.NEXT_PUBLIC_NOTIFICATIONS_PATH || "/notifications";

export interface NotificationsResponseItem {
  id: number;
  title: string;
  document?: GuidelineDocument | null;
  created_at?: string;
  updated_at?: string;
}

export const getNotificationsList = async () => {
  return axiosApplication.get<{
    success: boolean;
    message: string;
    data: NotificationsResponseItem[];
  }>(NOTIFICATIONS_PATH, {
    headers: { "Content-Type": "application/json" }
  });
};
